<script setup lang="ts" generic="T">
import { enumEq } from "~/utils/helpers"

const props = defineProps({
  options: null,
  label: { type: Function },
  loading: { type: Boolean },
  direction: null,
  disabled: { type: Boolean },
  disableOption: { type: Function }
})
const value = defineModel<T>({ required: true })

function handleSelect(key: T, toggle: () => void) {
  if (props.disabled || props.disableOption?.(key))
    return
  value.value = key
  toggle()
}
</script>

<template>
  <dropdown
    anchor-x="left"
    anchor-y="top"
    flow-x="right"
    :flow-y="direction ?? 'down'"
  >
    <template #default="{ toggle }">
      <div
        class="inline-select-button"
        :class="{ disabled }"
        @click="!disabled && toggle()"
      >
        <div>{{ label(value) }}</div>
        <interface-spinner
          v-if="loading"
          class="h-2 w-2"
        />
        <icon-action
          v-else
          size="2xs"
          type="chevron-down"
          class="-mr-1"
        />
      </div>
    </template>
    <template #menu="{ toggle }">
      <dropdown-menu class="inline-select-container">
        <interface-scrollbar vertical class="inline-select-scrollbar">
          <div class="inline-select-dropdown">
            <div
              v-for="option, key in options"
              :key
              class="inline-select-option t-menu-sm"
              :class="{ disabled: disableOption?.(option) }"
              @click="handleSelect(option as T, toggle)"
            >
              <div>{{ label(option as T) }}</div>
              <icon-action
                v-if="enumEq(value, option)"
                size="xs"
                type="check"
                class="icon"
              />
              <div v-else class="icon" />
            </div>
          </div>
        </interface-scrollbar>
      </dropdown-menu>
    </template>
  </dropdown>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'
.inline-select-button
  @apply text-$global-t-secondary t-menu-sm
  @apply flex cursor-pointer items-center gap-1 rounded-[#{$menu-br-item}] p-1 px-1.5 -m-1 -mx-1.5 h-6
  &.disabled
    @apply cursor-not-allowed opacity-50
  &:not(.disabled):hover
    @apply bg-[#{$menu-bg-item-hover}]

.inline-select-scrollbar
  @apply overflow-auto
  max-height: calc(100vh - 60px)

.inline-select-dropdown
  @apply flex flex-col gap-[#{$menu-gap-items}] px-[#{$menu-px-default}] py-[#{$menu-py-default}]

.inline-select-option
  @apply text-[#{$global-t-default}]
  @apply flex cursor-pointer items-center h-5 -m-1 p-1 gap-2
  @apply rounded-[#{$menu-br-item}]
  .icon
    @apply ml-auto h-4 w-4 text-[#{$global-bg-icon-secondary}]
  &.disabled
    @apply cursor-not-allowed opacity-50
  &:not(.disabled):hover
    @apply bg-[#{$menu-bg-item-hover}]
</style>
